import { FeaturePermissions } from '@order-management/roles';
import {
  DeferredResponse,
  DeviceHomeAlertData,
  SalesOrder,
  SoftwareHomeAlertData,
} from '@order-management/types';
import { Suspense, useCallback, useMemo } from 'react';
import { Columns } from 'react-bulma-components';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
  useNavigate,
} from 'react-router-dom';
import { fetchDevicesHomeAlertData } from '../../devices/devices-service';
import SalesOrdersList from '../../sales-orders/sales-orders-list/sales-orders-list';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import HomeCards from '../../shared/home-cards/home-cards';
import HomeHeader from '../../shared/home-header/home-header';
import useUser from '../../shared/hooks/user';
import useUserHasZspaceInternalAndCustomerRoles from '../../shared/hooks/user-has-zspace-internal-and-customer-roles';
import useUserPendingTasks from '../../shared/hooks/user-pending-tasks';
import If from '../../shared/if/if';
import ProtectedPage from '../../shared/protected-page/protected-page';
import { fetchSoftwareSeatsHomeAlertData } from '../../software/software-service';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import styles from './customers-home-page.module.scss';

type LoaderFunctionData = [DeviceHomeAlertData, SoftwareHomeAlertData];

export const loader: LoaderFunction = async () => {
  const devicesHomeAlertResponse = fetchDevicesHomeAlertData();
  const softwareSeatsHomeAlertResponse = fetchSoftwareSeatsHomeAlertData();

  return defer({
    response: Promise.all([
      devicesHomeAlertResponse,
      softwareSeatsHomeAlertResponse,
    ]),
  });
};

function CustomersHomePageContent() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [devicesTasksData, softwareTasksData] =
    useAsyncValue() as LoaderFunctionData;
  const userTasks = useUserPendingTasks(devicesTasksData, softwareTasksData);
  const hasZspaceInternalAndCustomerRoles =
    useUserHasZspaceInternalAndCustomerRoles();

  const hasAlert = useMemo(() => {
    return !![
      ...userTasks.devicesPendingTasks,
      ...userTasks.softwarePendingTasks,
    ].length;
  }, [userTasks.devicesPendingTasks, userTasks.softwarePendingTasks]);

  const activePermissionGroupsSalesOrders = useMemo(() => {
    const salesOrders =
      user.activeRole?.permissionsGroupUserRoleAssignments.reduce(
        (acc, permissionsGroupUserRoleAssignment) => {
          const salesOrder = permissionsGroupUserRoleAssignment.salesOrder;

          if (salesOrder && !acc.has(salesOrder.id)) {
            acc.set(salesOrder.id, salesOrder);
          }
          return acc;
        },
        new Map<string, Pick<SalesOrder, 'id' | 'number'>>()
      ) ?? new Map<string, Pick<SalesOrder, 'id' | 'number'>>();
    return Array.from(salesOrders.values());
  }, [user.activeRole?.permissionsGroupUserRoleAssignments]);

  const navigateToMyDevices = useCallback(() => {
    navigate('/my-devices');
  }, [navigate]);

  const navigateToMySoftware = useCallback(() => {
    navigate('/my-software');
  }, [navigate]);

  const navigateToMySalesOrders = useCallback(() => {
    navigate('/my-sales-orders');
  }, [navigate]);

  const navigateToMyTeam = useCallback(() => {
    navigate('/users/my-team');
  }, [navigate]);

  const headerSubtitle = useMemo(() => {
    if (hasZspaceInternalAndCustomerRoles) {
      return (
        <>
          <h3
            className={`is-size-6 has-text-primary-dark has-text-weight-light mb-5`}
          >
            For the following sales orders
          </h3>
          <h3 className={`is-size-6 has-text-weight-light mb-8`}>
            <SalesOrdersList
              list={activePermissionGroupsSalesOrders}
              hasMoreLink={`/users/${user.id}`}
              openLinksInNewTab
              maxElements={5}
            />
          </h3>
        </>
      );
    } else {
      return (
        <If condition={hasAlert}>
          <h3
            className={`is-size-6 has-text-weight-light has-text-primary-dark mb-8`}
          >
            You have actions pending. Resolve them to take full advantage of
            your zSpace products.
          </h3>
        </If>
      );
    }
  }, [
    activePermissionGroupsSalesOrders,
    hasAlert,
    hasZspaceInternalAndCustomerRoles,
    user.id,
  ]);

  const homeCards = useMemo(() => {
    const cards = [
      {
        title: 'My Sales Orders',
        description: 'Review your sales order history and their contents.',
        pendingTasks: [],
        onClick: navigateToMySalesOrders,
      },
      {
        title: 'My Team',
        description:
          'View all the users in your organization. Invite new ones and edit current permissions.',
        pendingTasks: [],
        onClick: navigateToMyTeam,
      },
      {
        title: 'My Devices',
        description:
          'View and rearrange all the devices in your organization and their software assignments.',
        pendingTasks: userTasks.devicesPendingTasks,
        onClick: navigateToMyDevices,
      },
      {
        title: 'My Software',
        description:
          'View all your current software titles and to which devices they have been assigned.',
        pendingTasks: userTasks.softwarePendingTasks,
        onClick: navigateToMySoftware,
      },
    ];

    return cards;
  }, [
    userTasks.devicesPendingTasks,
    userTasks.softwarePendingTasks,
    navigateToMySalesOrders,
    navigateToMyTeam,
    navigateToMyDevices,
    navigateToMySoftware,
  ]);

  return (
    <BoxLayout
      className={`is-min-height-80 mx-10 my-4 ${styles.layout}`}
      childrenClassName={styles.container}
    >
      <Columns
        display="flex"
        flexDirection="column"
        className="is-min-height-80"
      >
        <Columns.Column display="flex" flexDirection="column" p={4}>
          <HomeHeader children={headerSubtitle} />
        </Columns.Column>
        <Columns.Column>
          <HomeCards cards={homeCards} />
        </Columns.Column>
      </Columns>
    </BoxLayout>
  );
}

export function CustomersHomePage() {
  const { response } = useLoaderData() as DeferredResponse<LoaderFunctionData>;

  return (
    <ProtectedPage permissions={FeaturePermissions.RENEWALS_ACCESS}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <CustomersHomePageContent />
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default CustomersHomePage;
